import { Mic, MicOff, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { VoiceMode } from "@shared/chatPreferences";

interface VoiceModeToggleProps {
  mode: VoiceMode;
  onModeChange: (mode: VoiceMode) => void;
  disabled?: boolean;
}

const NEXT_MODE: Record<VoiceMode, VoiceMode> = {
  off: "record",
  record: "speech",
  speech: "off",
};

const MODE_META: Record<VoiceMode, { label: string; tooltip: string }> = {
  off: { label: "Voice", tooltip: "Voice off — tap for Record mode" },
  record: { label: "Record", tooltip: "Record mode: tap the mic to dictate — tap for Speech mode" },
  speech: { label: "Speech", tooltip: "Speech mode: two-way voice conversation — tap to turn off" },
};

/** Three-state voice mode toggle: Off → Record → Speech → Off. */
export function VoiceModeToggle({ mode, onModeChange, disabled = false }: VoiceModeToggleProps) {
  const meta = MODE_META[mode];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={mode === "off" ? "ghost" : "default"}
          size="sm"
          onClick={() => onModeChange(NEXT_MODE[mode])}
          disabled={disabled}
          className="h-8 px-2 gap-1.5"
          data-testid="button-voice-mode"
          aria-label={`Voice mode: ${mode}. Tap to switch.`}
        >
          {mode === "off" && <MicOff className="h-4 w-4" />}
          {mode === "record" && <Mic className="h-4 w-4" />}
          {mode === "speech" && <Volume2 className="h-4 w-4" />}
          <span className="text-xs hidden sm:inline">{meta.label}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{meta.tooltip}</p>
      </TooltipContent>
    </Tooltip>
  );
}
